import extraMeta from 'video/shared/extraMeta';
import { getContinuousPlayEnabledTrackingStatus } from 'video/shared/smart-tag-adapter/continous-play';
import { isDVRStreamType } from 'video/shared/smart-tag-adapter/context/smart-tag-adapter-context-tools';
import { isNFL } from 'video/shared/trackingHelper';

export class SmartTagAdapterContextBuilder {
    paramsVO;
    metaData;
    smartTagParams;
    CONTENT;
    utag_data;
    cpHBTracking;
    apiMetadata;
    showId;
    videoTitle;
    dma;
    carouselId;

    constructor(paramsVO) {
        this.paramsVO = paramsVO;
    }

    static createInstance(paramsVO) {
        return new this(paramsVO);
    }

    setMetaData(metaData) {
        this.metaData = metaData;
        return this;
    }

    setSmartTagParams(smartTagParams) {
        this.smartTagParams = smartTagParams;
        return this;
    }

    setContent(content) {
        this.CONTENT = content;
        return this;
    }

    setUtagData(utag_data) {
        this.utag_data = utag_data;
        return this;
    }

    setCpHBTracking(cpHBTracking) {
        this.cpHBTracking = cpHBTracking;
        return this;
    }

    setAPIMetaData(apiMetadata) {
        this.apiMetadata = apiMetadata;
        return this;
    }

    setShowId(showId) {
        this.showId = showId;
        return this;
    }

    setVideoTitle(videoTitle) {
        this.videoTitle = videoTitle;
        return this;
    }

    setDma(dma) {
        this.dma = dma;
        return this;
    }

    setCarouselId(id) {
        this.carouselId = id;
        return this;
    }

    computeData() {
        this.setGeneralMetadata(this.paramsVO);
        this.setCPMetadata(this.paramsVO);
        this.setLiveTVMetadata(this.paramsVO);
        this.setExtraMetadata(this.paramsVO);
    }

    setGeneralMetadata(paramsVO) {
        const utag_data = this.utag_data;

        if (this.CONTENT?.millstone?.rulesMatched !== undefined) {
            paramsVO.CONTEXT.metadata.rulesMatched = this.CONTENT.millstone.rulesMatched;
        }

        // episodeFlag is the boolean for enabling sparrow tracking
        // Pass through context.metadata vs. paramsVO.CONTENT.resource.metadata
        paramsVO.CONTEXT.metadata.episodeFlag = !!this?.metaData?.fullEpisode ?? true;

        paramsVO.CONTEXT.metadata.autoPlayEnabled = getContinuousPlayEnabledTrackingStatus() ?? '1';

        if (paramsVO.CONTEXT.metadata.pageType === 'movie') {
            if (localStorage.getItem('trackMovieCta')) {
                paramsVO.CONTEXT.metadata.ctaText = localStorage.getItem('trackMovieCta');
                localStorage.removeItem('trackMovieCta');
            }

            paramsVO.CONTEXT.metadata.pageType = 'video';
        }
        paramsVO.CONTEXT.metadata.primaryCategory =
            this.apiMetadata?.[0]?.['primaryCategory'] ?? '';
        paramsVO.CONTEXT.metadata.copyright = this.apiMetadata?.[0]?.['copyright'] ?? '';
        paramsVO.CONTEXT.metadata.bundleStatus = this.smartTagParams.bundleStatus ?? '';
        paramsVO.CONTEXT.metadata.airDate = this.metaData?.airdate_iso || '';
        paramsVO.CONTEXT.metadata.tvAirDate = this.metaData?.airdate_tv || '';
        if (this.smartTagParams.isLive) {
            paramsVO.CONTEXT.metadata.showGenre =
                this.apiMetadata?.[0]?.genre?.length > 0 ? this.apiMetadata[0]?.genre : 'other';
        }
        paramsVO.CONTEXT.metadata.mediaType =
            this.smartTagParams.isLive === true
                ? this.smartTagParams.liveStreamMetadata?.mediaType || ''
                : this.metaData?.mediaType || '';
        paramsVO.CONTEXT.metadata.partner = this.smartTagParams.partner || '';
        paramsVO.CONTEXT.metadata.country = this.smartTagParams.userCountry || '';
        paramsVO.CONTEXT.metadata.videoId = this.smartTagParams._contentID || '';
        paramsVO.CONTEXT.metadata.streamType = this.smartTagParams.isLive === true ? 'LIVE' : 'VOD';

        if (isDVRStreamType(this.metaData, CBS?.Registry?.channelInfo)) {
            paramsVO.CONTEXT.metadata.streamType = 'DVR';
        }

        paramsVO.CONTEXT.metadata.videoTitle =
            this.videoTitle !== '' ? this.videoTitle : this.utag_data.showEpisodeTitle;
        paramsVO.CONTEXT.metadata.liveVideoTitle =
            this.smartTagParams.isLive === true ? this.videoTitle : '';
        paramsVO.CONTEXT.metadata.episodeTitle =
            this.smartTagParams.isLive === true
                ? this.smartTagParams.liveStreamMetadata?.label || ''
                : this.metaData.label || '';
        paramsVO.CONTEXT.metadata.contentBrand =
            this.smartTagParams.isLive === true
                ? this.smartTagParams.liveStreamMetadata?.brand || ''
                : this.metaData.brand || '';
        paramsVO.CONTEXT.metadata.showSeriesId = this.showId || '';
        paramsVO.CONTEXT.metadata.videoProperties =
            this.smartTagParams.isLive === true
                ? this.smartTagParams.liveStreamMetadata?.videoProperties || []
                : this.metaData.videoProperties || [];
        paramsVO.CONTEXT.metadata.videoProperties =
            paramsVO.CONTEXT.metadata.videoProperties.join(', ');

        // For VOD to Live with DRM
        if (
            this.smartTagParams.CONTEXT &&
            this.smartTagParams.CONTEXT.metadata &&
            this.smartTagParams.CONTEXT.metadata.drmName
        ) {
            paramsVO.CONTEXT.metadata.drmName = this.smartTagParams.CONTEXT.metadata.drmName;
        } else {
            paramsVO.CONTEXT.metadata.drmName = paramsVO.CONTEXT.metadata.drmName || 'none';
        }
        paramsVO.CONTEXT.metadata.userRegId = paramsVO.CONTEXT.metadata.userRegId || 'anon';
        paramsVO.CONTEXT.metadata.packageSource = this.smartTagParams.packageSource ?? '';
        paramsVO.CONTEXT.metadata.packageStatus =
            this.smartTagParams.packageStatus ?? CBS.Registry.user.packageStatus ?? '';
        paramsVO.CONTEXT.metadata.multiPackageTracking =
            this.smartTagParams.multiPackageTracking ?? '';
        paramsVO.CONTEXT.metadata.vendorCode =
            this.smartTagParams.vendorCode ?? CBS.Registry.user.vendorCode ?? '';

        if (paramsVO.CONTEXT.metadata.streamType) {
            paramsVO.CONTEXT.metadata.isDcrLive = this.smartTagParams.isLive
                ? ['etl', 'cbsn', 'cbs-news', 'sports_hq', 'DVR', 'LIVE'].includes(
                      paramsVO.CONTEXT.metadata.streamType,
                  )
                : false;
        } else {
            paramsVO.CONTEXT.metadata.isDcrLive = false;
        }

        paramsVO.CONTEXT.metadata.rowHeaderTitle = this.metaData?.rowHeaderTitle || '';
        paramsVO.CONTEXT.metadata.posRowNum = this.metaData?.posRowNum || '';
        paramsVO.CONTEXT.metadata.posColNum = this?.metaData?.posColNum || '';
        paramsVO.CONTEXT.metadata.showSeasonNumber = this.metaData?.seasonNumber || '';
        paramsVO.CONTEXT.metadata.showEpisodeNumber = this.metaData?.episodeNumber || '';
        paramsVO.CONTEXT.metadata.seriesTitle =
            this.smartTagParams.liveStreamMetadata &&
            this.smartTagParams.liveStreamMetadata.seriesTitle
                ? this.smartTagParams.liveStreamMetadata.seriesTitle
                : '';
        paramsVO.CONTEXT.metadata.showTitle = utag_data.seriesTitle || '';
        paramsVO.CONTEXT.metadata.browserVersion = this.metaData?.browserVersion;
    }

    setCPMetadata(paramsVO) {
        if (this.cpHBTracking?.contPlayState) {
            Object.keys(this.cpHBTracking).forEach((key) => {
                paramsVO.CONTEXT.metadata[key] = this.cpHBTracking[key];
            });
        }
        if (this.carouselId) {
            paramsVO.CONTEXT.metadata.carouselId = this.carouselId;
        }
    }

    setLiveTVMetadata(paramsVO) {
        const utag_data = this.utag_data;

        if (this.smartTagParams.isLive) {
            if (localStorage.getItem('channelBrowseCategory')) {
                paramsVO.CONTEXT.metadata.channelBrowseCategory =
                    localStorage.getItem('channelBrowseCategory');
                localStorage.removeItem('channelBrowseCategory');
            } else {
                paramsVO.CONTEXT.metadata.channelBrowseCategory = 'all';
            }
            paramsVO.CONTEXT.metadata.dma = this.dma;
            paramsVO.CONTEXT.metadata.posColNum = sessionStorage.getItem('posColNum');
            paramsVO.CONTEXT.metadata.posRowNum = sessionStorage.getItem('posRowNum');
            paramsVO.CONTEXT.metadata.mediaSvodContentType = this.smartTagParams.isPaidContent
                ? 'paid'
                : 'free';
            paramsVO.CONTEXT.metadata._pageType = 'video';
            paramsVO.CONTEXT.metadata.isLive = true;
            paramsVO.CONTEXT.metadata.referenceProfileId = 0;
            if (localStorage.getItem('on_now_carousel')) {
                const _liveTvChannel = JSON.parse(localStorage.getItem('on_now_carousel'));
                paramsVO.CONTEXT.metadata.liveTvChannel =
                    _liveTvChannel.liveTvChannel ?? 'undefined';
                paramsVO.CONTEXT.metadata.stationCode = _liveTvChannel.stationCode ?? 'undefined';
                paramsVO.CONTEXT.metadata.channelBrowseCategory = 'all';
                localStorage.removeItem('on_now_carousel');
            }
            if (this.smartTagParams.channelName) {
                paramsVO.CONTEXT.metadata.liveTvChannel =
                    this.smartTagParams.channelName == 'CBS News Live Stream'
                        ? 'CBS News'
                        : this.smartTagParams.channelName;
            } else {
                const channelInfo = paramsVO.CONTENT.channelInfo;
                if (isNFL(channelInfo)) {
                    utag_data.episodeTitle = undefined;
                }
                paramsVO.CONTEXT.metadata.liveTvChannel = isNFL(channelInfo)
                    ? paramsVO.CONTENT?.channelInfo?.channelName
                    : (this.smartTagParams.liveStreamMetadata?.channelName ??
                      this.smartTagParams.liveStreamMetadata?.label);
            }
            //Fix so sparrow can be triggered
            paramsVO.CONTEXT.metadata.episodeFlag = true;
            paramsVO.CONTEXT.metadata.isEpisode = true;
        }
    }

    setExtraMetadata(paramsVO) {
        // extra metadata-video-tracking
        const obj = extraMeta();
        if (obj) {
            paramsVO.CONTEXT.metadata.rowHeaderTitle = obj.rowHeaderTitle;
            paramsVO.CONTEXT.metadata.posRowNum = obj.posRowNum;
            paramsVO.CONTEXT.metadata.posColNum = obj.posColNum;
            paramsVO.CONTEXT.metadata.showSeasonNumber =
                this.metaData.seasonNumber ?? obj.showSeasonNumber ?? '';
            paramsVO.CONTEXT.metadata.showEpisodeNumber =
                this.metaData.episodeNumber ?? obj.showEpisodeNumber ?? '';
        }

        if (localStorage.getItem('trackHeroCtaText')) {
            const trackHeroCtaText = JSON.parse(localStorage.getItem('trackHeroCtaText'));
            paramsVO.CONTEXT.metadata.ctaText = trackHeroCtaText.ctaText;
            localStorage.removeItem('trackHeroCtaText');
        }

        if (localStorage.getItem('trackMovieBrowseTileSelect')) {
            const objMovie = JSON.parse(localStorage.getItem('trackMovieBrowseTileSelect'));
            paramsVO.CONTEXT.metadata.pageType = 'video';
            paramsVO.CONTEXT.metadata.screenName = objMovie.screenName;
            paramsVO.CONTEXT.metadata.movieBrowseCategory = objMovie.movieBrowseCategory;
            paramsVO.CONTEXT.metadata.movieGenre = objMovie.movieGenre;
            paramsVO.CONTEXT.metadata.movieId = objMovie.movieId;
            paramsVO.CONTEXT.metadata.movieTitle = objMovie.movieTitle;
            paramsVO.CONTEXT.metadata.PPlusSubscriberBadge = objMovie.PPlusSubscriberBadge;
            localStorage.removeItem('trackMovieBrowseTileSelect');
        }

        // is this a schedule referral?
        if (localStorage.getItem('cbs_nsr') == 1) {
            paramsVO.CONTEXT.metadata.networkSelectionReferral = 1;
            localStorage.setItem('cbs_nsr', 0);
        }

        // START ABTest: homepage descriptions
        paramsVO.CONTEXT.metadata.carouselTile = this.metaData?.carouselTile || '';
        paramsVO.CONTEXT.metadata.spliceEnabled = this.metaData?.spliceEnabled || '';
        // END ABTest: homepage descriptions

        if (
            paramsVO.CONTEXT.metadata?.spliceEnabled == '' &&
            localStorage.getItem('spliceEnabled')
        ) {
            paramsVO.CONTEXT.metadata['spliceEnabled'] = localStorage.getItem('spliceEnabled');
            localStorage.removeItem('spliceEnabled');
        }
    }
}
